import { queryKeys } from '@/lib/query-keys';
import { orgMembersClient } from '@/lib/api/ts-rest';
import type { OrganizationRole } from '@octave/contracts';
import { createLogger } from '@/lib/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const log = createLogger('useOrganizationMemberOperations');

interface MemberOperationParams {
  organizationId: string;
  userId: string;
}

export function useDisableMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, userId }: MemberOperationParams) => {
      const res = await orgMembersClient.disableMember({
        params: { organizationId, memberId: userId },
      });
      if (res.status !== 201) throw new Error('Failed to disable member');
      return res.body;
    },
    onSuccess: (_, { organizationId }) => {
      toast.success('Membre désactivé avec succès');
      // Invalidate the members list to refresh the UI
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
    onError: (error) => {
      toast.error('Erreur lors de la désactivation du membre');
      log.error('Error disabling member', error);
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      userId,
      role,
    }: MemberOperationParams & { role: OrganizationRole }) => {
      const res = await orgMembersClient.updateMemberRole({
        params: { organizationId, memberId: userId },
        body: { role },
      });
      if (res.status !== 200) throw new Error('Failed to update role');
      return res.body;
    },
    onSuccess: (_, { organizationId }) => {
      toast.success('Rôle mis à jour avec succès');
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
    onError: (error) => {
      log.error('Error updating member role', error);
      toast.error('Erreur lors de la mise à jour du rôle');
    },
  });
}

export function useUpdateMemberName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      userId,
      firstName,
      lastName,
    }: MemberOperationParams & { firstName: string; lastName: string }) => {
      const res = await orgMembersClient.updateMemberName({
        params: { organizationId, memberId: userId },
        body: { firstName, lastName },
      });
      if (res.status !== 200) throw new Error('Failed to update name');
      return res.body;
    },
    onSuccess: (_, { organizationId }) => {
      toast.success('Nom mis à jour avec succès');
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
    onError: (error) => {
      log.error('Error updating member name', error);
      toast.error('Erreur lors de la mise à jour du nom');
    },
  });
}

export function useEnableMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, userId }: MemberOperationParams) => {
      const res = await orgMembersClient.enableMember({
        params: { organizationId, memberId: userId },
      });
      if (res.status !== 201) throw new Error('Failed to enable member');
      return res.body;
    },
    onSuccess: (_, { organizationId }) => {
      toast.success('Membre activé avec succès');
      // Invalidate the members list to refresh the UI
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
    onError: (error) => {
      toast.error("Erreur lors de l'activation du membre");
      log.error('Error enabling member', error);
    },
  });
}
